<script setup lang="ts">
import { storeToRefs } from "pinia";
import { GroupsApi } from "@simbelapp/order-sdk";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import TagDropdown from "~/components/Common/Dropdowns/TagDropdown.vue";
import { useAuthStore } from "~/store/auth/auth.store";
import { useBookingsStore } from "~/store/bookings/bookings.store";
import { useCompaniesStore } from "~/store/companies/companies.store";
import { useProjectsStore } from "~/store/project/projects.store";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import type { IGroup } from "~/utils/interfaces/users-interfaces";

const authStore = useAuthStore();
const bookingsStore = useBookingsStore();
const companiesStore = useCompaniesStore();
const projectsStore = useProjectsStore();
const sessionsStore = useSessionsStore();
const sessionIndividual = useSessionIndividual();

const { legalEntities } = storeToRefs(companiesStore);
const { trainingPlanProjectIndividual } = storeToRefs(projectsStore);
const { trainingPlanSessionIndividual } = storeToRefs(sessionsStore);
const { tmpBookingLegalEntity, tmpBookingGroups } = storeToRefs(bookingsStore);

const legalEntitiesOptions = computed(() => legalEntities.value ?? []);
const isProjectMode = computed(() => sessionIndividual.sourceType.value === ItemType.PROJECT);
const isReadonly = computed(() => isProjectMode.value || (!authStore.isCompanyAdmin && !authStore.isGroupAdmin));
const currentBooking = computed(() =>
  isProjectMode.value ? trainingPlanProjectIndividual.value : trainingPlanSessionIndividual.value,
);

onMounted(async () => {
  // Legal entities only feed the dropdown options, and the endpoint is admin-scoped: fetching them
  // while it's disabled would 403 and, having no try/catch, reject before the init below runs.
  if (!isReadonly.value) await companiesStore.fetchLegalEntities();

  const booking = currentBooking.value;
  if (booking?.legalEntityId && booking?.legalEntity) {
    tmpBookingLegalEntity.value = {
      companyId: booking.legalEntityId,
      name: booking.legalEntity,
    };
  } else {
    tmpBookingLegalEntity.value = null;
  }
  // The booking carries its groups with their names, so no company-wide group list is needed here.
  tmpBookingGroups.value = booking?.userGroups ?? [];
});

const servicesAPI = async (params: any) => {
  const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);
  const feedback = useFeedback();
  try {
    if (!isReadonly.value) {
      const data = await apiInstance.getAvailableGroupsByRoles({
        lazyLoad: true,
        enableRolesFilters: false,
        ...params,
      });
      return data;
    }
  } catch {
    feedback.error("Une erreur est survenue", "small");
  }
};
</script>

<template>
  <div class="session_assignment__container">
    <h3 class="heading_3_demi text-primary-color p-[8px]">Entité légale et groupes</h3>
    <div class="session_assignment__grid">
      <section>
        <div class="session_assignment__items">
          <div class="flex items-center">
            <h2>Entité de l'inscription</h2>
          </div>
          <SimpleDropdown
            v-model="tmpBookingLegalEntity"
            searchable
            :delete="false"
            :disabled="isReadonly"
            :options="legalEntitiesOptions"
            placeholder="Sélectionner une entité légale"
            track-by="companyId"
            label="name"
          >
            <template #singleLabel="{ option }">
              {{ option.name }}
            </template>
            <template #option="{ option }">
              {{ option.name }}
            </template>
          </SimpleDropdown>
        </div>
      </section>
      <section>
        <div class="session_assignment__items">
          <div class="flex items-center">
            <h2>Groupe(s) de l'inscription</h2>
          </div>
          <TagDropdown
            v-model="tmpBookingGroups"
            class="w-full"
            :disabled="isReadonly"
            :loading="!authStore.availableGroupsByRoles"
            :options="authStore.availableGroupsByRoles"
            :store="authStore"
            store-object="availableGroupsByRoles"
            placeholder="Choisissez une option"
            label="name"
            track-by="groupId"
            :services-a-p-i="servicesAPI"
          >
            <template #tag="{ option }">
              <div class="w-full whitespace-break-spaces py-1 leading-normal">{{ option.name }}</div>
            </template>
            <template #option="{ option }">
              <span class="w-full whitespace-break-spaces py-1 leading-normal">{{ option.name }} </span>
            </template>
          </TagDropdown>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.session_assignment__container {
  @apply pt-[8px];
  border-bottom: 1px solid var(--grey-lightest);
}

.session_assignment__grid {
  @apply flex flex-col gap-[16px] my-[16px] px-[8px] w-full;

  section {
    @apply flex flex-col gap-[16px];
  }

  h2 {
    @apply text-secondary-color font-[14px];
  }
}

.session_assignment__items {
  @apply flex flex-col gap-[8px] w-full;
}
</style>
